import { create } from "zustand";
import { NAME_DISPLAY_FORMAT } from "../constants/AppConstants";
import { formatUserDisplayName } from "../pages/utils/commonUtils";

const toOsyncUserIdKey = (osyncUserId) =>
    osyncUserId == null || osyncUserId === "" ? "" : String(osyncUserId);

let usersByOsyncUserId = new Map();
let displayNameByOsyncUserId = new Map();
let licensedUsers = [];
let nameDisplayFormat = NAME_DISPLAY_FORMAT.FIRST_LAST;
let currentUser = null;

export const readNameDisplayFormat = () => nameDisplayFormat;

const findUser = (osyncUserId) => {
    const key = toOsyncUserIdKey(osyncUserId);
    return key ? usersByOsyncUserId.get(key) : undefined;
};

const buildLookups = () => ({
    getUserDisplayName: ({ osyncUserId } = {}) => displayNameByOsyncUserId.get(toOsyncUserIdKey(osyncUserId)) || "",
    getUserId: ({ osyncUserId } = {}) => {
        const user = findUser(osyncUserId);
        return user?.osyncUserId == null || user.osyncUserId === "" ? "" : String(user.osyncUserId);
    },
    getRemoteUserId: ({ osyncUserId } = {}) => findUser(osyncUserId)?.remoteUserId || "",
    // For payloads that carry only the CRM user (e.g. a campaign's createdBy JSON), not an osyncUserId.
    getUserDisplayNameByRemoteUser: ({ remoteUserId, email } = {}) => {
        const remoteUserIdKey = remoteUserId == null || remoteUserId === "" ? "" : String(remoteUserId);
        const emailKey = email?.trim?.()?.toLowerCase?.() || "";
        if (!remoteUserIdKey && !emailKey) {
            return "";
        }
        const userByRemoteUserId = remoteUserIdKey
            ? licensedUsers?.find((licensedUser) => String(licensedUser?.remoteUserId ?? "") === remoteUserIdKey)
            : undefined;
        const user =
            userByRemoteUserId ||
            (emailKey ? licensedUsers?.find((licensedUser) => licensedUser?.email?.toLowerCase?.() === emailKey) : undefined);
        return displayNameByOsyncUserId.get(toOsyncUserIdKey(user?.osyncUserId)) || "";
    },
});

const currentUserFields = () => {
    const currentUserId = toOsyncUserIdKey(currentUser?.osyncUserId);
    const licensedUser = currentUserId ? usersByOsyncUserId.get(currentUserId) : undefined;
    return {
        currentUserId: licensedUser?.osyncUserId == null || licensedUser?.osyncUserId === "" ? currentUserId : String(licensedUser.osyncUserId),
        currentRemoteUserId: licensedUser?.remoteUserId || currentUser?.remoteUserId || "",
        currentUserDisplayName: currentUserId ? displayNameByOsyncUserId.get(currentUserId) || "" : "",
    };
};

export const useUserDetailsStore = create(() => ({
    currentUserDisplayName: "",
    currentUserId: "",
    currentRemoteUserId: "",
    ...buildLookups(),
}));

export const syncUserDetails = ({ users, nameDisplayFormat: nextNameDisplayFormat, currentUser: nextCurrentUser } = {}) => {
    const format = nextNameDisplayFormat || NAME_DISPLAY_FORMAT.FIRST_LAST;
    const list = Array.isArray(users) ? users : [];
    if (licensedUsers === list && nameDisplayFormat === format && currentUser === nextCurrentUser) {
        return;
    }
    licensedUsers = list;
    nameDisplayFormat = format;
    currentUser = nextCurrentUser || null;
    const nextUsers = new Map();
    const nextDisplayNames = new Map();
    list.forEach((user) => {
        const key = toOsyncUserIdKey(user?.osyncUserId);
        if (!key) {
            return;
        }
        nextUsers.set(key, user);
        nextDisplayNames.set(key, formatUserDisplayName(user, format));
    });
    usersByOsyncUserId = nextUsers;
    displayNameByOsyncUserId = nextDisplayNames;
    useUserDetailsStore.setState({
        ...currentUserFields(),
        ...buildLookups(),
    });
};
